import axios from 'axios';
import { API_BASE_URL } from '../constants';

const TOKEN_KEY = 'resume_updater_token';

export function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY) || '';
}

export function setStoredToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

const api = axios.create({
  baseURL: API_BASE_URL,
});

api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Reads that hit a restart or a dropped connection are retried for about 30 s (a deploy or a
// restart) before giving up; writes (scrape, generate, save) are never retried so nothing runs twice.
// Long actions do not wait in one request at all: they run as server jobs (api/jobs.js).
const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000];
const RETRY_METHODS = new Set(['get', 'head', 'options']);

function isTransient(error) {
  const status = error?.response?.status;
  if (!error.response) return error.code !== 'ERR_CANCELED';
  return status === 502 || status === 503 || status === 504;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config;
    const method = String(config?.method || 'get').toLowerCase();
    if (config && !config.__noRetry && RETRY_METHODS.has(method) && isTransient(error)) {
      const attempt = config.__retryCount || 0;
      if (attempt < RETRY_DELAYS_MS.length) {
        config.__retryCount = attempt + 1;
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));
        return api(config);
      }
    }
    const status = error?.response?.status;
    const isRead = RETRY_METHODS.has(method);
    if (!error.response && error.code === 'ECONNABORTED') {
      error.message = isRead
        ? 'The server took too long to answer. Try again.'
        : 'The server is taking long to finish this. It may still complete — refresh in a minute to check before trying again.';
    } else if (!error.response && error.code !== 'ERR_CANCELED') {
      error.message = isRead
        ? 'Could not reach the server (no connection, or it is restarting). It reconnects by itself — try again shortly.'
        : 'The connection dropped before the server answered (it may be restarting). Refresh in a minute to check whether it was saved before trying again.';
    } else if (status === 502 || status === 503 || status === 504) {
      const detail = error?.response?.data?.detail;
      error.message = typeof detail === 'string' && detail.trim()
        ? detail
        : 'The server is restarting. Try again in a minute.';
    }
    return Promise.reject(error);
  },
);

export function authDownloadUrl(path) {
  const token = getStoredToken();
  if (!token) return `${API_BASE_URL}${path}`;
  const join = path.includes('?') ? '&' : '?';
  return `${API_BASE_URL}${path}${join}token=${encodeURIComponent(token)}`;
}

/** Encode meeting/resume ids for use in URL path segments. */
export function encodeId(id) {
  return encodeURIComponent(String(id ?? ''));
}

/** API path segment for a meeting id. */
export function meetingApiPath(meetingId, suffix = '') {
  const base = `/api/meetings/${encodeId(meetingId)}`;
  return suffix ? `${base}${suffix.startsWith('/') ? suffix : `/${suffix}`}` : base;
}

/** Resume download URL — prefers meeting-scoped routes so caller access matches the interview. */
export function meetingResumeDownloadUrl(meeting, format = 'pdf') {
  if (meeting?.id != null && meeting.id !== '') {
    if (format === 'txt') {
      return authDownloadUrl(meetingApiPath(meeting.id, '/resume/txt'));
    }
    return authDownloadUrl(meetingApiPath(meeting.id, '/resume'));
  }
  if (meeting?.stored_resume_id) {
    const ext = format === 'txt' ? 'txt' : 'pdf';
    const id = encodeId(meeting.stored_resume_id);
    return authDownloadUrl(`/api/meetings/stored-resumes/${id}/${ext}`);
  }
  if (meeting?.resume_is_url && meeting?.resume_path) {
    return meeting.resume_path;
  }
  return '#';
}

/** Normalize FastAPI/axios error detail into a display string. */
export function apiErrorText(err, fallback = 'Request failed.') {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const parts = detail.map((item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object') return item.msg || item.message || '';
      return '';
    }).filter(Boolean);
    if (parts.length) return parts.join(' ');
  }
  if (detail && typeof detail === 'object' && detail.msg) return String(detail.msg);
  if (err?.message) return err.message;
  return fallback;
}

export default api;
